import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import MpTopbar from "@/components/marketplace/MpTopbar";
import MpFooter from "@/components/marketplace/MpFooter";
import MpSearchBar from "@/components/marketplace/MpSearchBar";
import VenueCard from "@/components/marketplace/VenueCard";
import GMapEmbed from "@/components/marketplace/GMapEmbed";
import { SlidersHorizontal, Map as MapIcon, List, X } from "lucide-react";
import "@/styles/marketplace.css";

/**
 * Marketplace Search Results (split-pane).
 * Left: filter chips + venue cards
 * Right: live Leaflet map with rating pins
 * On mobile: toggle between List / Map full-screen.
 */
export default function MarketplaceResults() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const q = searchParams.get("q") || "";
  const cat = searchParams.get("cat") || searchParams.get("category") || "";
  const muni = searchParams.get("muni") || "";
  const area = searchParams.get("area") || "";
  const initialAreaId = searchParams.get("area_id") || "";

  const [listings, setListings] = useState([]);
  const [areas, setAreas] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState("list"); // list | map

  // Load taxonomy.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, m, c] = await Promise.all([
          api.get("/marketplace/areas?country=qa"),
          api.get("/marketplace/municipalities"),
          api.get("/marketplace/categories"),
        ]);
        if (!alive) return;
        setAreas(a.data?.items || []);
        setMunicipalities(m.data?.items || []);
        setCategories(c.data?.items || []);
      } catch (_e) { /* swallow — keep page rendering */ }
    })();
    return () => { alive = false; };
  }, []);

  // Load listings whenever the URL changes.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (cat) params.set("category", cat);
        // Accept ?area=... (preferred, used by the home search bar) OR
        // the legacy ?area_id=... param.
        const effArea = area || initialAreaId;
        if (effArea) params.set("area", effArea);
        params.set("limit", "60");
        const r = await api.get(`/marketplace/search?${params.toString()}`);
        if (!alive) return;
        setListings(r.data?.items || []);
      } catch (_e) { /* swallow */ } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [q, cat, area, initialAreaId]);

  // Filter client-side by municipality if needed (search API uses area_id).
  const filtered = useMemo(() => {
    if (!muni) return listings;
    const municipalityAreaIds = areas.filter(a => a.municipality === muni).map(a => a.area_id);
    return listings.filter(l => (l.area_ids || []).some(aid => municipalityAreaIds.includes(aid)));
  }, [listings, muni, areas]);

  // The search index stores a representative `geo: {type: Point, coordinates: [lng,lat]}`
  // per listing (NOT a `branches[]` array — that's only on the full listing doc).
  // Build map pins from that geo when present.
  const mapMarkers = useMemo(() => filtered
    .filter((l) => l.geo && Array.isArray(l.geo.coordinates) && l.geo.coordinates.length === 2)
    .map((l) => ({
      id: l.listing_id || l.slug,
      lng: l.geo.coordinates[0],
      lat: l.geo.coordinates[1],
      name: (l.name && (l.name[isAr ? "ar" : "en"] || l.name.en)) || "Salon",
      rating: l.rating?.avg || 0,
      area: l.area_label || "",
      slug: l.slug,
    })), [filtered, isAr]);

  const clearFilter = (key) => {
    const params = new URLSearchParams(searchParams);
    params.delete(key);
    navigate(`/m?${params.toString()}`);
  };

  return (
    <div className="mp-root" data-testid="mp-results" dir={isAr ? "rtl" : "ltr"}>
      <MpTopbar variant="solid" />

      {/* Sticky search bar */}
      <div className="border-b border-[var(--mp-border)] bg-white sticky top-[68px] z-30">
        <div className="mp-container py-3">
          <MpSearchBar variant="compact" />
        </div>
      </div>

      {/* Active filter chips */}
      {(q || cat || muni) && (
        <div className="mp-container py-3 flex flex-wrap items-center gap-2 border-b border-[var(--mp-divider)]">
          <span className="text-xs text-[var(--mp-ink-muted)] me-1">{isAr ? "فلاتر:" : "Filters:"}</span>
          {q && <Chip label={`“${q}”`} onClear={() => clearFilter("q")} />}
          {cat && <Chip label={cat} onClear={() => clearFilter("cat")} />}
          {muni && <Chip label={municipalities.find(m => m.municipality === muni)?.name?.[isAr ? "ar" : "en"] || muni} onClear={() => clearFilter("muni")} />}
        </div>
      )}

      {/* Mobile list/map toggle */}
      <div className="md:hidden mp-container py-3 flex gap-2">
        <button onClick={() => setMobileView("list")} className={`mp-chip flex-1 justify-center ${mobileView === "list" ? "mp-chip-active" : ""}`}>
          <List size={14} /> {isAr ? "قائمة" : "List"}
        </button>
        <button onClick={() => setMobileView("map")} className={`mp-chip flex-1 justify-center ${mobileView === "map" ? "mp-chip-active" : ""}`}>
          <MapIcon size={14} /> {isAr ? "خريطة" : "Map"}
        </button>
      </div>

      {/* Split pane */}
      <div className="mp-container py-6 flex flex-col md:flex-row gap-6 min-h-[600px]">
        <div className={`md:w-[55%] ${mobileView === "map" ? "hidden md:block" : ""}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">
              {loading ? (isAr ? "..." : "...") : `${filtered.length} ${isAr ? "نتيجة" : "results"}`}
            </h2>
            <button className="mp-btn mp-btn-ghost !py-1.5 !px-3 text-xs">
              <SlidersHorizontal size={13} /> {isAr ? "ترتيب" : "Sort"}
            </button>
          </div>
          {loading && <div className="text-center py-12 text-[var(--mp-ink-muted)]">{isAr ? "جاري التحميل..." : "Loading..."}</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 mp-card-flat" data-testid="mp-no-results">
              <div className="text-2xl mb-2">🔍</div>
              <div className="font-bold">{isAr ? "لم نجد نتائج" : "No matches found"}</div>
              <div className="text-sm text-[var(--mp-ink-muted)] mt-2">{isAr ? "جرّب تعديل الفلاتر أو البحث في منطقة أخرى." : "Try adjusting filters or search a different area."}</div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map(l => <VenueCard key={l.listing_id} l={l} />)}
          </div>
        </div>

        <div className={`md:w-[45%] md:sticky md:top-[140px] md:self-start ${mobileView === "list" ? "hidden md:block" : ""}`}>
          {mapMarkers.length === 0 ? (
            <div className="h-[400px] rounded-xl bg-stone-50 border border-dashed border-stone-200 flex flex-col items-center justify-center text-stone-500 text-sm">
              <MapIcon size={28} className="mb-2 opacity-50" />
              <div>{isAr ? "لا توجد فروع بإحداثيات لعرضها" : "No branches with coordinates"}</div>
            </div>
          ) : (
            <div className="space-y-3">
              <GMapEmbed
                lat={mapMarkers[0].lat}
                lng={mapMarkers[0].lng}
                zoom={12}
                height="h-[400px]"
                testId="mp-results-map"
                label={isAr
                  ? `يعرض موقع: ${mapMarkers[0].name}`
                  : `Showing: ${mapMarkers[0].name}`}
              />
              {mapMarkers.length > 1 && (
                <div className="bg-white rounded-xl border border-stone-200 p-3 max-h-[280px] overflow-y-auto" data-testid="mp-results-locations-list">
                  <div className="text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">
                    {isAr ? `جميع المواقع (${mapMarkers.length})` : `All locations (${mapMarkers.length})`}
                  </div>
                  <ul className="space-y-1">
                    {mapMarkers.map((m) => (
                      <li key={m.id}>
                        <a
                          href={`https://www.google.com/maps?q=${m.lat},${m.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-stone-50 text-sm transition"
                          data-testid={`mp-results-location-${m.id}`}
                        >
                          <span className="flex items-center gap-2 truncate">
                            <MapIcon size={13} className="text-[var(--mp-maroon)] shrink-0" />
                            <span className="font-semibold truncate">{m.name}</span>
                            {m.area && <span className="text-xs text-stone-400 truncate">· {m.area}</span>}
                          </span>
                          <span className="text-xs text-stone-400 shrink-0">{isAr ? "فتح" : "Open"}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <MpFooter />
    </div>
  );
}

function Chip({ label, onClear }) {
  return (
    <span className="mp-chip mp-chip-active gap-1.5 !py-1 !px-3 !text-xs">
      {label}
      <button onClick={onClear} className="hover:bg-white/20 rounded-full p-0.5"><X size={11} /></button>
    </span>
  );
}
